"use client";

import React, { useRef, useEffect } from 'react';
import { cn } from '@/lib/utils';
import { renderIcon } from '../../editor/renderIcon';

export interface DropdownMenuEditableItemProps {
  // Icon button props
  iconButtonRef?: React.RefObject<HTMLButtonElement>;
  icon?: React.ReactNode; // Icon or emoji to display in the button
  onIconClick?: () => void;
  iconButtonDisabled?: boolean;
  iconButtonAriaLabel?: string;
  
  // Input field props
  inputValue: string;
  inputOnChange: (value: string) => void;
  inputOnFocus?: () => void;
  inputOnBlur?: () => void;
  inputOnKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  inputPlaceholder?: string;
  inputAriaLabel?: string;
  inputDisabled?: boolean;
  /** Focus + select the input text once on mount. */
  autoFocusSelect?: boolean;

  // Container props
  className?: string;
  children?: React.ReactNode; // For popups/modals (like emoji picker)
}

export function DropdownMenuEditableItem({
  iconButtonRef: externalIconButtonRef,
  icon,
  onIconClick,
  iconButtonDisabled = false,
  iconButtonAriaLabel = "Change icon",
  inputValue,
  inputOnChange,
  inputOnFocus,
  inputOnBlur,
  inputOnKeyDown,
  inputPlaceholder = "Name",
  inputAriaLabel = "Name",
  inputDisabled = false,
  autoFocusSelect = false,
  className,
  children,
}: DropdownMenuEditableItemProps) {
  const internalIconButtonRef = useRef<HTMLButtonElement>(null);
  const iconButtonRef = externalIconButtonRef || internalIconButtonRef;
  const inputRef = useRef<HTMLInputElement>(null);

  // Focus + select on mount; 0ms timeout lets the modal mount first.
  useEffect(() => {
    if (!autoFocusSelect || inputDisabled) return;
    const timer = setTimeout(() => {
      const el = inputRef.current;
      if (el) {
        el.focus();
        el.select();
      }
    }, 0);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className={cn("px-[14px] pb-1.5", className)}>
      <div className="flex items-center gap-2 w-full relative">
        {/* Icon button */}
        {icon !== undefined && (
          <button
            ref={iconButtonRef}
            type="button"
            onClick={onIconClick}
            className="flex items-center justify-center p-1.5 w-[30px] h-[30px] flex-shrink-0 text-sec border border-component rounded-[7px] hover-component  transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
            disabled={iconButtonDisabled}
          >
            {typeof icon === 'string' ? (
              <span className="text-base flex items-center justify-center" aria-hidden="true">
                {renderIcon(icon, { style: { width: 16, height: 16 } })}
              </span>
            ) : (
              <span aria-hidden="true">
                {icon}
              </span>
            )}
          </button>
        )}
        
        {/* Input field */}
        <input
          ref={inputRef}
          value={inputValue}
          onChange={(e) => inputOnChange(e.target.value)}
          onFocus={inputOnFocus}
          onBlur={inputOnBlur}
          onKeyDown={inputOnKeyDown}
          placeholder={inputPlaceholder}
          className="w-full h-[30px] px-[9px] text-sm border rounded-[7px] focus:outline-none focus:ring-2 focus:ring-blue-500 bg-[color-mix(in_srgb,var(--hover-bg),transparent_45%)] dark:text-gray-100 border-component"
          disabled={inputDisabled}
        />
        
        {/* Children (for popups/modals like emoji picker) */}
        {children}
      </div>
    </div>
  );
}

